import React from "react";
import styled, { keyframes } from "styled-components";
import { ITheme } from "../Themes/ThemeTypes";
import { SkeletonShapeEnum } from "./SkeletonEnums";

interface SkeletonProps {
  shape?: SkeletonShapeEnum;
  width?: number | string;
  height?: number | string;
}

const PULSE_DURATION_MS = 1500;

const pulse = keyframes`
  50% { opacity: 0.5; }
`;

const toCss = (v: number | string): string => (typeof v === "number" ? `${v}px` : v);

const radiusFor = (shape: SkeletonShapeEnum): string => {
  switch (shape) {
    case SkeletonShapeEnum.CIRCLE:
      return "50%";
    case SkeletonShapeEnum.TEXT:
      return "2px";
    case SkeletonShapeEnum.RECTANGLE:
    default:
      return "4px";
  }
};

const defaultHeight = (shape: SkeletonShapeEnum, width: number | string): number | string => {
  if (shape === SkeletonShapeEnum.CIRCLE) return typeof width === "number" ? width : 40;
  return 16;
};

const Box = styled.div<{
  theme: ITheme;
  $shape: SkeletonShapeEnum;
  $width: string;
  $height: string;
}>`
  display: block;
  width: ${(props) => props.$width};
  height: ${(props) => props.$height};
  border-radius: ${(props) => radiusFor(props.$shape)};
  background-color: rgba(${(props) => props.theme.colors.on_surface}, 0.2);
  animation: ${pulse} ${PULSE_DURATION_MS}ms
    ${(props) => props.theme.base.motion.easing.ease_in_out} infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Skeleton: React.FC<SkeletonProps> = ({
  shape = SkeletonShapeEnum.RECTANGLE,
  width = "100%",
  height,
}) => {
  const resolvedHeight = height ?? defaultHeight(shape, width);
  return <Box $shape={shape} $width={toCss(width)} $height={toCss(resolvedHeight)} />;
};

export default Skeleton;
